import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MDBSpinner, MDBTooltip } from "mdb-react-ui-kit";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../services";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { subscribeToApp } from "../services/accountService";
import "./service-intermediary.css";

const DELIVERY_TYPES = new Set(["JAHEZ", "KEETA", "CHEFZ", "THE_CHEFZ", "NINJA"]);
const POS_TYPES = new Set(["FOODICS", "TABSENSE"]);
const BRAND_LABELS = [
  ["the chefz", "The Chefz"],
  ["digital catalog", "Digital Catalog"],
  ["track hub", "Track Hub"],
  ["chat agent", "Chat Agent"],
  ["tab sense", "TabSense"],
  ["tabsense", "TabSense"],
  ["foodics", "Foodics"],
  ["jahez", "Jahez"],
  ["keeta", "Keeta"],
  ["chefz", "Chefz"],
  ["ninja", "Ninja"],
  ["noon", "Noon"],
];

function brandLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase();
  const found = BRAND_LABELS.find(([token]) => token === key);
  if (found) return found[1];
  if (raw === key) return raw.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
  return raw;
}

function presentPlanName(rawName, appName) {
  const label = brandLabel(appName);
  const pattern = [...BRAND_LABELS]
    .map(([token]) => token.replace(/\s+/g, "\\s+"))
    .sort((left, right) => right.length - left.length)
    .join("|");
  const named = label
    ? String(rawName || "").replace(new RegExp(`\\b(?:${pattern})\\b`, "gi"), label)
    : String(rawName || "");
  return named.replace(/\b[a-z][a-z0-9]*\b/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}

function money(value, language) {
  const amount = Number(value || 0);
  return amount.toLocaleString(language || undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function familyKey(app) {
  if (!app) return "integration";
  if (app.category === "ADDON") return "addon";
  if (POS_TYPES.has(app.appType)) return "pos";
  if (DELIVERY_TYPES.has(app.appType)) return "delivery";
  return "integration";
}

function setupPath(app) {
  if (!app?.id) return "/apps-management";
  if (app.appType === "TRACK_HUB") return `/trackhub-integration/${app.id}`;
  if (app.category === "ADDON") return `/addon-integration/${app.id}`;
  return `/add-app/${app.id}`;
}

function planFeatures(plan, isArabic) {
  if (isArabic && plan?.features_ar?.length) return plan.features_ar;
  return plan?.features || [];
}

function featureParts(feature, isArabic) {
  if (feature && typeof feature === "object") {
    return {
      title: (isArabic && feature.title_ar) || feature.title || feature.label || "",
      description: (isArabic && feature.description_ar) || feature.description || feature.desc || "",
    };
  }
  const text = String(feature || "");
  const [title, ...rest] = text.split("\n");
  return { title, description: rest.join(" ").trim() };
}

function shortPlanName(name) {
  return String(name || "").replace(/^(monthly|annual|yearly)\s+/i, "");
}

function scrollToPlans() {
  document.getElementById("service-plans")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function ServiceIntermediaryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutState = searchParams.get("checkout");
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const { can } = usePermission();
  const canPerformSubscription = can(RBAC_MODULES.SUBSCRIPTIONS, PERMISSION_ACTIONS.PERFORM_SUBSCRIPTION);
  const canSubscribe = can(RBAC_MODULES.APP_MANAGEMENT, PERMISSION_ACTIONS.ADD_INTEGRATIONS) && canPerformSubscription;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadPage = useCallback(async () => {
    const response = await api.get(`/available-apps/${id}/service-page`);
    setPage(response.data);
    return response.data;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        if (checkoutState && canPerformSubscription) {
          await api.post("/subscriptions/sync", { availableAppId: id }).catch(() => null);
        }
        if (!cancelled) await loadPage();
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.message || t("servicePage.loadError"));
          navigate("/apps-management", { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, checkoutState, canPerformSubscription, loadPage, navigate, t]);

  const app = page?.app;
  const subscription = page?.subscription;
  const servicesUnlocked = page?.servicesUnlocked === true;
  const plans = page?.plans || [];
  const currentRank = plans.find((plan) => plan.isCurrent)?.rank ?? -1;
  const activeSubscription = subscription?.status === "Active";

  const displayName = brandLabel((isArabic && app?.name_ar) || app?.name || "");
  const planAppName = app?.name || displayName;
  const displayDescription = (isArabic && app?.description_ar) || app?.description || "";
  const highlights = page?.benefits?.length
    ? page.benefits
    : ((isArabic && page?.highlights_ar?.length ? page.highlights_ar : page?.highlights) || []);

  const freemiumPlan = useMemo(() => {
    if (!page?.freemium) return null;
    return {
      code: "FREEMIUM",
      name: t("servicePage.freemium"),
      amountSar: 0,
      taxSar: 0,
      currency: "SAR",
      interval: "month",
      intervalCount: 1,
      trialDays: 0,
      features: page?.defaultIncluded?.en || [],
      features_ar: page?.defaultIncluded?.ar || [],
      chip: page?.defaultIncluded?.chip || null,
      chip_ar: page?.defaultIncluded?.chip_ar || null,
      badge: null,
      rank: 0,
      isCurrent: activeSubscription && !subscription?.planCode,
      freemium: true,
    };
  }, [page?.freemium, page?.defaultIncluded, t, activeSubscription, subscription?.planCode]);

  const visiblePlans = freemiumPlan ? [freemiumPlan] : plans;

  const openPlan = async (plan) => {
    setSelectedPlan(plan);
    setQuote(null);
    if (plan.freemium) {
      setQuote({
        name: plan.name,
        dueTodaySar: 0,
        amountSar: 0,
        taxSar: 0,
        trialDays: 0,
        currency: "SAR",
        features: plan.features || [],
        features_ar: plan.features_ar || [],
        chip: plan.chip || null,
        chip_ar: plan.chip_ar || null,
        interval: "month",
        intervalCount: 1,
      });
      return;
    }
    setQuoteLoading(true);
    try {
      const response = await api.get("/subscriptions/quote", {
        params: { availableAppId: id, planCode: plan.code },
      });
      setQuote(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || t("servicePage.quoteError"));
      setSelectedPlan(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  const confirmPlan = async () => {
    if (!selectedPlan) return;
    setSubmitting(true);
    try {
      if (selectedPlan.freemium) {
        await subscribeToApp(id);
        toast.success(t("servicePage.subscribeSuccess"));
        setSelectedPlan(null);
        await loadPage();
        return;
      }
      navigate("/subscription");
    } catch (error) {
      toast.error(error.response?.data?.message || t("servicePage.checkoutError"));
    } finally {
      setSubmitting(false);
    }
  };

  const planAction = (plan) => {
    if (plan.isCurrent) return "current";
    if (subscription?.status === "Pending" && subscription.planCode === plan.code) return "pending";
    if (activeSubscription && subscription?.planCode && plan.rank > currentRank) return "upgrade";
    if (!activeSubscription || !subscription?.planCode) return "subscribe";
    return "unavailable";
  };

  const actionLabel = (action) => {
    if (action === "current") return t("servicePage.currentPlan");
    if (action === "pending") return t("servicePage.paymentPending");
    if (action === "upgrade") return t("servicePage.upgrade");
    return t("servicePage.subscribe");
  };

  const lockedReason = !canSubscribe
    ? t("noPermissionToAccessThis")
    : !servicesUnlocked
      ? t("servicePage.approvalRequired")
      : "";

  const resumeCheckout = (plan) => {
    if (subscription?.checkoutUrl && subscription.planCode === plan.code) {
      window.location.assign(subscription.checkoutUrl);
      return;
    }
    openPlan(plan);
  };

  const renderPlanButton = (plan) => {
    const action = planAction(plan);
    const canResume = action === "pending" && Boolean(subscription?.checkoutUrl);
    const disabled = Boolean(lockedReason) || action === "current" || action === "unavailable" || (action === "pending" && !canResume);
    const button = (
      <button
        type="button"
        className="service-plan-btn"
        disabled={disabled}
        onClick={() => (canResume ? resumeCheckout(plan) : openPlan(plan))}
      >
        {action === "upgrade"
          ? t("servicePage.selectUpgrade", { name: shortPlanName(presentPlanName(plan.name, planAppName)) })
          : action === "subscribe"
            ? t("servicePage.selectPlan", { name: shortPlanName(presentPlanName(plan.name, planAppName)) })
            : actionLabel(action)}
      </button>
    );
    if (!lockedReason || action === "current") return button;
    return (
      <MDBTooltip tag="div" title={lockedReason} wrapperProps={{ className: "w-100" }}>
        {button}
      </MDBTooltip>
    );
  };

  if (loading || !app) {
    return (
      <div className="service-page oh-page d-flex justify-content-center py-5">
        <MDBSpinner role="status" />
      </div>
    );
  }

  const showFailure = checkoutState === "failed" || checkoutState === "cancelled" || checkoutState === "canceled";
  const showWaiting = checkoutState === "success" && subscription?.status === "Pending";
  const included = quote
    ? (isArabic && quote.features_ar?.length ? quote.features_ar : quote.features || planFeatures(selectedPlan, isArabic))
    : [];
  const headerTrialDays = visiblePlans.reduce((max, plan) => Math.max(max, Number(plan.trialDays) || 0), 0);
  const modalPlanTitle = presentPlanName(quote?.name || selectedPlan?.name, planAppName);
  const modalName = shortPlanName(modalPlanTitle);
  const modalFeature = (isArabic && quote?.chip_ar) || quote?.chip || selectedPlan?.chip || "";
  const intervalSuffix = selectedPlan?.interval === "year" ? t("servicePage.perYear") : t("servicePage.perMonth");
  const trialDays = Math.max(Number(quote?.trialDays) || 0, Number(selectedPlan?.trialDays) || 0);
  const dueToday = trialDays > 0 ? 0 : Number(quote?.dueTodaySar ?? quote?.amountSar ?? 0);
  const requirements = Array.isArray(page?.requirements) ? page.requirements : [];

  const headerAction = activeSubscription ? (
    <button type="button" className="service-primary" onClick={() => navigate(setupPath(app))}>
      {t("servicePage.continueSetup")}
    </button>
  ) : lockedReason ? (
    <MDBTooltip tag="span" title={lockedReason}>
      <button type="button" className="service-primary" disabled>
        {t("servicePage.subscribe")}
      </button>
    </MDBTooltip>
  ) : (
    <button type="button" className="service-primary" onClick={() => navigate("/subscription")}>
      {t("servicePage.subscribe")}
    </button>
  );

  return (
    <div className="service-page oh-page" dir={i18n.dir()} lang={i18n.language}>
      {showFailure ? (
        <div className="service-banner service-banner--warning">{t("servicePage.paymentFailed")}</div>
      ) : null}
      {showWaiting ? (
        <div className="service-banner">{t("servicePage.paymentWaiting")}</div>
      ) : null}

      <section className="service-card service-header">
        <div className="service-header-main">
          <div className="service-logo service-logo--letter">
            <span>{(displayName.trim().slice(0, 1) || "J").toLocaleUpperCase(isArabic ? "ar" : "en")}</span>
          </div>
          <div>
            <h1>{displayName}</h1>
            {headerTrialDays > 0 ? (
              <p className="service-trial-line">{t("servicePage.trialDays", { count: headerTrialDays })}</p>
            ) : null}
            <span className="service-chip">{t(`servicePage.family.${familyKey(app)}`)}</span>
          </div>
        </div>
        {headerAction}
      </section>

      <section className="service-hero">
        <h2>{t("servicePage.heroTitle", { name: displayName })}</h2>
        {displayDescription ? <p>{displayDescription}</p> : null}
        <div className="service-hero-path">
          <span className="service-pill service-pill--light">{t("servicePage.loopsStore")}</span>
          <span className="service-hero-arrow" aria-hidden>→</span>
          <span className="service-pill service-pill--brand">{displayName}</span>
        </div>
        <div className="service-hero-actions">
          <button type="button" className="service-view-plans" onClick={scrollToPlans}>
            {t("servicePage.viewPlans")}
          </button>
        </div>
      </section>

      {highlights.length > 0 ? (
        <section className="service-card">
          <h3>{t("servicePage.whatYouGet")}</h3>
          <ul className="service-feature-grid">
            {highlights.map((item) => {
              const parts = featureParts(item, isArabic);
              return (
                <li key={parts.title}>
                  <strong>{parts.title}</strong>
                  {parts.description ? <span>{parts.description}</span> : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="service-card">
        <h3>{t("servicePage.howToConnect")}</h3>
        <ol className="service-requirements">
          {requirements.map((item, index) => (
            <li key={item.key || index}>
              <span>{index + 1}.</span>
              <p>{t(`servicePage.requirements.${item.key}`, { name: displayName, defaultValue: item.label })}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="service-card" id="service-plans">
        <h3>{t("servicePage.plans")}</h3>
        {page.plansError ? <p className="service-muted">{t("servicePage.plansUnavailable")}</p> : null}
        {!visiblePlans.length ? <p className="service-muted">{t("servicePage.noPlans")}</p> : null}
        <div className="service-plan-grid">
          {visiblePlans.map((plan) => {
            const features = planFeatures(plan, isArabic);
            const planTitle = presentPlanName(plan.name, planAppName);
            const subtitle = (isArabic && plan.subtitle_ar) || plan.subtitle;
            const period = plan.interval === "year" ? t("servicePage.perYear") : t("servicePage.perMonth");
            const popular = plan.badge === "most_popular";
            return (
              <article key={plan.code} className={`service-plan${popular ? " service-plan--popular" : ""}`}>
                {popular ? (
                  <div className="service-plan-badge">{t("servicePage.mostPopular")}</div>
                ) : null}
                {plan.isCurrent ? <div className="service-plan-current">{t("servicePage.currentPlan")}</div> : null}
                <h4>{planTitle}</h4>
                <p className="service-plan-price">
                  {plan.compareAtSar ? (
                    <s>{t("servicePage.sar")} {money(plan.compareAtSar, i18n.language)}</s>
                  ) : null}
                  <strong>{money(plan.amountSar, i18n.language)}</strong>
                  <small>{t("servicePage.sar")} {period}</small>
                </p>
                {subtitle ? <p className="service-plan-subtitle">{subtitle}</p> : null}
                <ul>
                  {features.map((feature) => {
                    const parts = featureParts(feature, isArabic);
                    return <li key={parts.title}>{parts.title}</li>;
                  })}
                </ul>
                {renderPlanButton(plan)}
                {plan.trialDays > 0 ? (
                  <p className="service-plan-footnote">
                    {t("servicePage.thenPrice", {
                      count: plan.trialDays,
                      amount: money(plan.amountSar, i18n.language),
                      period,
                    })}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>

      <Modal
        show={Boolean(selectedPlan)}
        onHide={() => !submitting && setSelectedPlan(null)}
        centered
        dir={i18n.dir()}
        dialogClassName={`service-modal${isArabic ? " service-modal--rtl" : ""}`}
        contentClassName="service-modal-content"
      >
        <Modal.Body dir={i18n.dir()} lang={i18n.language} className="service-modal-body">
          {quoteLoading || !quote ? (
            <div className="d-flex justify-content-center py-4">
              <MDBSpinner role="status" />
            </div>
          ) : (
            <>
              <h2 className="service-modal-title">
                {t(planAction(selectedPlan || {}) === "upgrade" ? "servicePage.upgradeTitle" : "servicePage.subscribeTitle", {
                  name: modalName,
                })}
              </h2>
              <p className="service-modal-subtitle">
                {displayName}
                {modalFeature ? ` · ${modalFeature}` : ""}
              </p>

              <div className="service-modal-box">
                <h3>{t("servicePage.connectedPlatform")}</h3>
                <div className="service-modal-pills">
                  <span className="service-pill service-pill--brand">{displayName}</span>
                  {modalFeature ? <span className="service-pill service-pill--green">{modalFeature}</span> : null}
                </div>
              </div>

              {included.length > 0 ? (
                <div className="service-modal-box">
                  <h3>{t("servicePage.included")}</h3>
                  <ul className="service-modal-included">
                    {included.map((item) => {
                      const parts = featureParts(item, isArabic);
                      return <li key={parts.title}>{parts.title}</li>;
                    })}
                  </ul>
                </div>
              ) : null}

              <div className="service-modal-box service-quote-box">
                {trialDays > 0 ? (
                  <>
                    <div className="service-quote-row">
                      <span>{t("servicePage.trialDays", { count: trialDays })}</span>
                      <span>{t("servicePage.dueTodayAmount", { amount: money(0, i18n.language) })}</span>
                    </div>
                    <div className="service-quote-row">
                      <span>{t("servicePage.afterTrial")}</span>
                      <span>
                        {t("servicePage.sar")} {money(quote.amountSar, i18n.language)} {intervalSuffix}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="service-quote-row">
                    <span>{modalPlanTitle}</span>
                    <span>
                      {t("servicePage.sar")} {money(quote.amountSar, i18n.language)} {intervalSuffix}
                    </span>
                  </div>
                )}
                <div className="service-quote-row service-quote-row--due">
                  <strong>{t("servicePage.dueToday")}</strong>
                  <strong>
                    {t("servicePage.sar")} {money(dueToday, i18n.language)}
                  </strong>
                </div>
              </div>
              <p className="service-modal-note">{t("servicePage.trialNote")}</p>
              <div className="service-modal-actions">
                <button type="button" className="service-modal-cancel" disabled={submitting} onClick={() => setSelectedPlan(null)}>
                  {t("servicePage.cancel")}
                </button>
                <button type="button" className="service-modal-confirm" disabled={submitting} onClick={confirmPlan}>
                  {submitting
                    ? t("servicePage.working")
                    : dueToday > 0
                      ? t("servicePage.proceedToPayment")
                      : t(planAction(selectedPlan || {}) === "upgrade" ? "servicePage.confirmUpgrade" : "servicePage.confirmSubscribe", {
                          name: modalName,
                        })}
                </button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ServiceIntermediaryPage;
